import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useCursorContext } from '../hooks/useCursorContext';

interface NavigationProps {
  isLoaded: boolean;
}

const navItems = [
  { label: 'Arbeiten', href: '/work' },
  { label: 'Über mich', href: '/about' },
  { label: 'Erfahrung', href: '/experience' },
  { label: 'Kontakt', href: '/contact' },
];

export function Navigation({ isLoaded }: NavigationProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { setCursorState } = useCursorContext();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const handleHover = (hovering: boolean) => {
    setCursorState(hovering ? 'hovering' : 'default');
  };

  return (
    <AnimatePresence>
      {isLoaded && (
        <>
          {/* Main Navigation */}
          <motion.header
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
              isScrolled || isMobileMenuOpen ? 'bg-black/90 backdrop-blur-md' : ''
            }`}
          >
            <nav className="flex items-center justify-between px-6 py-5 md:px-12 md:py-6">
              {/* Logo */}
              <motion.div whileHover={{ scale: 1.05 }} className="relative z-50">
                <Link
                  to="/"
                  className="text-xl font-medium tracking-wider text-white"
                  onMouseEnter={() => handleHover(true)}
                  onMouseLeave={() => handleHover(false)}
                >
                  NK<span className="text-emerald-500">.</span>
                </Link>
              </motion.div>

              {/* Desktop Nav Links */}
              <div className="hidden md:flex items-center gap-8">
                {navItems.map((item, i) => {
                  const isActive = location.pathname === item.href;

                  return (
                    <motion.div
                      key={item.label}
                      initial={{ y: -20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.6 + i * 0.1 }}
                    >
                      <Link
                        to={item.href}
                        className={`text-sm transition-colors tracking-wider uppercase ${
                          isActive
                            ? 'text-emerald-500'
                            : 'text-white/60 hover:text-white'
                        }`}
                        onMouseEnter={() => handleHover(true)}
                        onMouseLeave={() => handleHover(false)}
                      >
                        {item.label}
                      </Link>
                    </motion.div>
                  );
                })}
              </div>

              {/* Right side: Availability + Mobile Menu Button */}
              <div className="flex items-center gap-4">
                {/* Availability indicator (desktop) */}
                <motion.div
                  initial={{ x: 20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.9 }}
                  className="hidden sm:flex items-center gap-2"
                >
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                  </span>
                  <span className="text-xs text-white/40 tracking-wider uppercase">
                    Available
                  </span>
                </motion.div>

                {/* Mobile Menu Button */}
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="relative z-50 md:hidden p-2 -mr-2 text-white"
                  onMouseEnter={() => handleHover(true)}
                  onMouseLeave={() => handleHover(false)}
                  aria-label={isMobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
                >
                  <AnimatePresence mode="wait">
                    {isMobileMenuOpen ? (
                      <motion.div
                        key="close"
                        initial={{ rotate: -90, opacity: 0 }}
                        animate={{ rotate: 0, opacity: 1 }}
                        exit={{ rotate: 90, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <X className="w-6 h-6" />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="menu"
                        initial={{ rotate: 90, opacity: 0 }}
                        animate={{ rotate: 0, opacity: 1 }}
                        exit={{ rotate: -90, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Menu className="w-6 h-6" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>
              </div>
            </nav>
          </motion.header>

          {/* Mobile Menu Overlay */}
          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="fixed inset-0 z-40 bg-black/95 backdrop-blur-xl md:hidden"
              >
                <div className="flex flex-col items-center justify-center h-full px-8">
                  {/* Mobile Nav Items */}
                  <nav className="flex flex-col items-center gap-8">
                    {navItems.map((item, i) => {
                      const isActive = location.pathname === item.href;

                      return (
                        <motion.div
                          key={item.label}
                          initial={{ y: 40, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          exit={{ y: -20, opacity: 0 }}
                          transition={{ delay: i * 0.1, duration: 0.4 }}
                        >
                          <Link
                            to={item.href}
                            className={`text-3xl font-light tracking-wider ${
                              isActive ? 'text-emerald-500' : 'text-white'
                            }`}
                            onMouseEnter={() => handleHover(true)}
                            onMouseLeave={() => handleHover(false)}
                          >
                            {item.label}
                          </Link>
                        </motion.div>
                      );
                    })}
                  </nav>

                  {/* Mobile Availability */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="absolute bottom-12 flex items-center gap-2"
                  >
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                    </span>
                    <span className="text-sm text-white/40 tracking-wider uppercase">
                      Verfügbar für Projekte
                    </span>
                  </motion.div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  );
}
